import { useCallback, useMemo, useState } from 'react';

import {
  useShareProgramItem,
  useUnshareProgramItem,
} from '../../../../redux/api/programsAPI';
import { useGetUserTrainingAccessIncoming } from '../../../../redux/api/userTrainingAccessApi';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import { IProgramItem } from '../../../../types/interfaces/program-item';
import { IUser } from '../../../../types/interfaces/user';
import { orderBy } from '../../../../utils/collections/array-utils';
import { getUserViewName } from '../../../../utils/common/shared-util';
import {
  accessRowToIUser,
  computeIdDelta,
} from '../../../users/settings/access-settings/access-settings-helpers';

export function useProgramShareAccess(
  programItem: IProgramItem | undefined,
  programItemId: string,
) {
  const { data: user } = useGetMyDetails();
  const {
    data: userTrainingAccess,
    isLoading: isAccessLoading,
    isFetching: isAccessFetching,
  } = useGetUserTrainingAccessIncoming();
  const [shareProgramItem] = useShareProgramItem();
  const [unshareProgramItem, { isLoading: isUnsharing }] =
    useUnshareProgramItem();
  const [isBulkWorking, setIsBulkWorking] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<IUser | null>(null);

  const shareProgramAllowedUsers = useMemo(
    () =>
      (userTrainingAccess?.sharePrograms ?? []).map((row) =>
        accessRowToIUser(row),
      ),
    [userTrainingAccess?.sharePrograms],
  );

  const modalUsers = useMemo(() => {
    let base = shareProgramAllowedUsers.map((u) => ({ ...u }));
    const existing = programItem?.availableToUsers ?? [];
    for (const u of existing) {
      if (u.id !== user?.id && !base.some((b) => b.id === u.id)) {
        base = [{ ...u }, ...base];
      }
    }
    const withName = base.map((u) => ({
      ...u,
      displayName: getUserViewName(u),
    }));
    return orderBy(withName, ['displayName'], ['asc']).filter(
      (u) => u.id !== user?.id,
    );
  }, [shareProgramAllowedUsers, programItem?.availableToUsers, user?.id]);

  const selectedUserIds = useMemo(
    () => (programItem?.availableToUsers ?? []).map((u) => u.id),
    [programItem?.availableToUsers],
  );

  const displayUsers = useMemo(() => {
    const list = programItem?.availableToUsers ?? [];
    const withName = list.map((u) => ({
      ...u,
      displayName: getUserViewName(u),
    }));
    return orderBy(withName, ['displayName'], ['asc']);
  }, [programItem?.availableToUsers]);

  const handleModalSave = useCallback(
    async (nextIds: string[]) => {
      const prevIds = (programItem?.availableToUsers ?? []).map((u) => u.id);
      const { add, remove } = computeIdDelta(prevIds, nextIds);
      if (add.length === 0 && remove.length === 0) {
        setDialogOpen(false);
        return;
      }
      setIsBulkWorking(true);
      try {
        await Promise.all([
          ...add.map((userId) =>
            shareProgramItem({ itemId: programItemId, userId }).unwrap(),
          ),
          ...remove.map((userId) =>
            unshareProgramItem({ itemId: programItemId, userId }).unwrap(),
          ),
        ]);
        setDialogOpen(false);
      } catch {
        // RTK toast
      } finally {
        setIsBulkWorking(false);
      }
    },
    [
      programItem?.availableToUsers,
      programItemId,
      shareProgramItem,
      unshareProgramItem,
    ],
  );

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) {
      return;
    }
    try {
      await unshareProgramItem({
        itemId: programItemId,
        userId: removeTarget.id,
      }).unwrap();
      setRemoveTarget(null);
    } catch {
      // RTK toast
    }
  }, [removeTarget, unshareProgramItem, programItemId]);

  const isPosting = isBulkWorking || isUnsharing;

  return {
    displayUsers,
    modalUsers,
    selectedUserIds,
    dialogOpen,
    setDialogOpen,
    removeTarget,
    setRemoveTarget,
    handleModalSave,
    handleConfirmRemove,
    isPosting,
    isAccessLoading,
    isAccessFetching,
  };
}
